import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, BookOpen, CheckCircle2, Circle, Bookmark,
  BookmarkCheck, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StudyMaterialTopics() {
  const { examId, subject } = useParams();
  const { token } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const decodedSubject = decodeURIComponent(subject);

  useEffect(() => {
    fetch(`${API_URL}/study-material/exam/${examId}/subject/${encodeURIComponent(decodedSubject)}/topics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setTopics(d.topics || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [examId, decodedSubject, token]);

  const completed = topics.filter(t => t.completed).length;
  const progress = topics.length > 0 ? (completed / topics.length) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="study-topics">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/study-material/${examId}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-to-subjects">
          <ArrowLeft className="w-4 h-4" />Back to Subjects
        </Link>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">{decodedSubject}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {examId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </p>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1">
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>{completed} of {topics.length} topics completed</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2.5" />
            </div>
          </div>
        </div>

        {/* Topic List */}
        <div className="space-y-2">
          {topics.map((topic, idx) => (
            <Link
              key={topic.topic_id}
              to={`/study-material/${examId}/${encodeURIComponent(decodedSubject)}/${topic.topic_id}?name=${encodeURIComponent(topic.name)}`}
              className={`group flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 ${
                topic.completed
                  ? 'border-emerald-500/20 bg-emerald-500/5 hover:border-emerald-500/40'
                  : 'border-border bg-card hover:border-primary/30 hover:shadow-md'
              }`}
              data-testid={`topic-${topic.topic_id}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                topic.completed
                  ? 'bg-emerald-500/20'
                  : 'bg-muted group-hover:bg-primary/10'
              }`}>
                {topic.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <span className="text-sm font-bold text-muted-foreground group-hover:text-primary">{idx + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm font-medium group-hover:text-primary transition-colors ${topic.completed ? 'text-emerald-400' : ''}`}>
                  {topic.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />~15 min
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {topic.bookmarked && (
                  <BookmarkCheck className="w-4 h-4 text-amber-400" />
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
